<template>
  <div class="container">
    <div class="columns">
      <div class="column">
        <div class="title is-4">{{ $t("debt level case") }}</div>
        <div class="subtitle is-5 mb-0">
          <span class="icon">
            <i class="fa fa-file-text"></i>
          </span>
          {{ $t("promissory note number") }}:
          <strong>{{ debt_level.promissory_note_number }}</strong>
        </div>
        <div class="subtitle is-6 mb-0" v-if="debt_level.sib_extracted_data">
          <span class="icon">
            <i class="fa fa-user"></i>
          </span>
          {{ $t("name") }}:
          <strong>{{ debt_level.sib_extracted_data.nombre_deudor }}</strong>
        </div>
        <div class="subtitle is-6" v-if="debt_level.sib_extracted_data">
          <span class="icon">
            <i class="fa fa-id-card"></i>
          </span>
          {{ $t("dpi") }}:
          <strong>{{ debt_level.sib_extracted_data.cui }}</strong>
        </div>
      </div>
      <div class="column">
        <div class="button is-primary is-rounded is-pulled-right" @click="save">
          <span class="icon">
            <i class="fa fa-save"></i>
          </span>
          <span>{{ $t("save") }}</span>
        </div>
      </div>
    </div>
    <div class="tabs is-toggle is-fullwidth" ref="tabs">
      <ul>
        <li
          class="is-active"
          data-tab="original"
          @click="change_active_tab($event, this)"
        >
          <a>
            <i class="info icon" aria-hidden="true"></i>
            <span>{{ $t("original") }}</span>
          </a>
        </li>
        <li data-tab="edited" @click="change_active_tab($event, this)">
          <a>
            <i class="edit icon mb-0" aria-hidden="true"></i>
            <span>{{ $t("edited") }}</span>
          </a>
        </li>
      </ul>
    </div>
    <div class="is-fluid" ref="contents">
      <div class="active" data-tab="original">
        <original ref="original" />
      </div>
      <div class="is-hidden" data-tab="edited">
        <edited ref="edited" />
      </div>
    </div>
  </div>
</template>

<script>
import {
  put,
  error_message,
  get,
  post,
  get_configuration,
  success_message,
  format_currency,
  format_date_time,
  generate_error_message_text,
  change_active_tab,
  get_currency_settings,
} from "@/utils";
import moment from "moment";
import { groupBy } from "lodash";

import Original from "./view/Original.vue";
import Edited from "./view/Edit.vue";

export default {
  name: "DebtLevelView",
  components: { Original, Edited },
  data() {
    return {
      meta: {},
      debt_level: {},
      error: "",
      module_settings: {},
    };
  },
  computed: {
    total_capital() {
      let v = this;
      let prestamos = 0;
      let tarjetas = 0;
      let extras = 0;
      let no_definidos = 0;

      if (v.meta.prestamos) {
        prestamos = v.meta.prestamos.reduce(
          (sum, item) => sum + item.capital_original,
          0
        );
      }
      if (v.meta.tarjetas) {
        tarjetas = v.meta.tarjetas.reduce(
          (sum, item) => sum + item.saldo_capital,
          0
        );
      }
      if (v.meta.extras) {
        extras = v.meta.extras.reduce(
          (sum, item) => sum + item.capital_original,
          0
        );
      }
      if (v.meta.no_definidos) {
        no_definidos = v.meta.no_definidos.reduce(
          (sum, item) => sum + item.capital_original,
          0
        );
      }

      return prestamos + tarjetas + extras + no_definidos;
    },
    total_quota() {
      let v = this;
      let prestamos = 0;
      let tarjetas = 0;
      let extras = 0;
      let no_definidos = 0;
      if (v.meta.prestamos) {
        prestamos = v.meta.prestamos.reduce((sum, item) => sum + item.quota, 0);
      }
      if (v.meta.tarjetas) {
        tarjetas = v.meta.tarjetas.reduce((sum, item) => sum + item.quota, 0);
      }
      if (v.meta.extras) {
        extras = v.meta.extras.reduce((sum, item) => sum + item.quota, 0);
      }
      // if (v.meta.no_definidos) {
      //   no_definidos = v.meta.no_definidos.reduce(
      //     (sum, item) => sum + item.quota,
      //     0
      //   );
      // }
      return prestamos + tarjetas + extras /* + no_definidos */;
    },
    liquid_salary() {
      let v = this;
      if (v.meta.nominal_salary) return v.meta.nominal_salary * 0.9;
      else return 0;
    },
    liquid_minus_debt() {
      let v = this;
      return v.liquid_salary - v.total_quota;
    },
    total_tarjetas() {
      let v = this;
      if (v.meta.tarjetas) {
        return v.meta.tarjetas.reduce(
          (sum, item) => sum + item.saldo_capital,
          0
        );
      } else return 0;
    },
    total_prestamos() {
      let v = this;
      if (v.meta.prestamos) {
        return v.meta.prestamos.reduce(
          (sum, item) => sum + item.capital_original,
          0
        );
      } else return 0;
    },
    total_extras() {
      let v = this;
      if (v.meta.extras) {
        return v.meta.extras.reduce(
          (sum, item) => sum + item.capital_original,
          0
        );
      } else return 0;
    },
    total_hipotecas() {
      let v = this;
      if (v.meta.hipotecas) {
        return v.meta.hipotecas.reduce(
          (sum, item) => sum + item.capital_original,
          0
        );
      } else return 0;
    },
    cuota_tarjetas() {
      let v = this;
      if (v.meta.tarjetas) {
        return v.meta.tarjetas.reduce((sum, item) => sum + item.quota, 0);
      } else return 0;
    },
    cuota_prestamos() {
      let v = this;
      if (v.meta.prestamos) {
        return v.meta.prestamos.reduce((sum, item) => sum + item.quota, 0);
      } else return 0;
    },
    cuota_extras() {
      let v = this;
      if (v.meta.extras) {
        return v.meta.extras.reduce((sum, item) => sum + item.quota, 0);
      } else return 0;
    },
    cuota_hipotecas() {
      let v = this;
      if (v.meta.hipotecas) {
        return v.meta.hipotecas.reduce((sum, item) => sum + item.quota, 0);
      } else return 0;
    },
    active_types() {
      return [
        { name: "credit card", color: "#f39c12" },
        { name: "extrafinancing", color: "#3498db" },
        { name: "mortgage", color: "#27ae60" },
        { name: "loan", color: "#e74c3c" },
        { name: "undefined", color: "#9b59b6" },
        { name: "cancelled", color: "#FFFFFF" },
      ];
    },
    minimum_debt_level() {
      let v = this;
      if (v.meta.minimum_quota) {
        return (
          ((v.meta.minimum_quota / v.liquid_minus_debt) * 100).toFixed(2) + "%"
        );
      } else return "0%";
    },
    minimum_liquidity() {
      let v = this;
      if (v.meta.minimum_quota) {
        return v.liquid_minus_debt - v.meta.minimum_quota;
      } else return "0";
    },
    maximum_debt_level() {
      let v = this;
      if (v.meta.maximum_quota) {
        return (
          ((v.meta.maximum_quota / v.liquid_minus_debt) * 100).toFixed(2) + "%"
        );
      } else return "0%";
    },
    maximum_liquidity() {
      let v = this;
      if (v.meta.maximum_quota) {
        return v.liquid_minus_debt - v.meta.maximum_quota;
      } else return "0";
    },
  },
  methods: {
    change_active_tab,
    refresh() {
      let v = this;
      v.$root.loader(true);
      let uuid = v.$route.params.uuid;
      get("/debt-level", { uuid: uuid })
        .then((debt_level) => {
          v.debt_level = debt_level;
          let data = debt_level.sib_extracted_data;
          if (data && data.detalle) {
            /** Tarjetas de crédito */
            let credit_card_conditions =
              v.module_settings.credit_card_conditions || [];
            let tarjetas = v.filter_data(
              data.detalle,
              credit_card_conditions,
              "credit card"
            );

            let grouped_tarjetas = groupBy(tarjetas, "entidad");
            data.tarjetas = Object.keys(grouped_tarjetas).map((key) => {
              let items = grouped_tarjetas[key];
              let total_capital_original = items.reduce(
                (sum, item) => sum + item.capital_original,
                0
              );
              let total_saldo_capital = items.reduce(
                (sum, item) => sum + item.saldo_capital,
                0
              );
              return {
                entidad: key,
                tipo_activo: "TARJETA DE CRÉDITO",
                capital_original: total_capital_original,
                plazo: 60,
                saldo_capital: total_saldo_capital,
                tasa_anual: 0.02, // Assuming a fixed annual rate for all credit cards
                quota: 0.02 * total_saldo_capital,
              };
            });

            /* Cancelados */
            let cancelados = data.detalle.filter(
              (item) =>
                item.saldo_capital === 0 &&
                !tarjetas.find((tarjeta) => tarjeta.id === item.id)
            );
            data.cancelados = cancelados;

            /** Extrafinanciamientos */
            let extrafinancing_conditions =
              v.module_settings.extrafinancing_conditions || [];
            let extras = v
              .filter_data(
                data.detalle,
                extrafinancing_conditions,
                "extrafinancing"
              )
              .filter(
                (item) =>
                  !cancelados.find((cancelado) => cancelado.id === item.id)
              );

            data.extras = extras.map((item) => {
              let fecha_concesion = item.concesion;
              let fecha_vencimiento = "";
              if (item.vencimiento) {
                fecha_vencimiento = item.vencimiento;
                fecha_vencimiento = fecha_vencimiento.replace(
                  /(\d{2})\/(\d{2})\/(\d{4})/,
                  "01/$2/$3"
                );
                fecha_concesion = fecha_concesion.replace(
                  /(\d{2})\/(\d{2})\/(\d{4})/,
                  "01/$2/$3"
                );
                let term_in_months = moment(
                  fecha_vencimiento,
                  "DD/MM/YYYY"
                ).diff(moment(fecha_concesion, "DD/MM/YYYY"), "months");
                item.plazo = term_in_months.toFixed(0);
                if (item.plazo >= 36)
                  item.tasa_anual = 0.2; // 20% annual rate for terms over 36 months
                else item.tasa_anual = 0;
                item.quota =
                  v.pmt(
                    item.tasa_anual / 12,
                    item.plazo,
                    item.capital_original
                  ) * -1;
              }
              return item;
            });

            /** Hipotecas */
            let mortgage_conditions =
              v.module_settings.mortgage_conditions || [];
            let hipotecas = v
              .filter_data(data.detalle, mortgage_conditions, "mortgage")
              .filter(
                (item) =>
                  !cancelados.find((cancelado) => cancelado.id === item.id)
              );
            data.hipotecas = hipotecas.map((item) => {
              item.tasa_anual = 0.2; // Assuming a fixed annual rate for non-credit card debts
              let fecha_concesion = item.concesion;
              let fecha_vencimiento = "";
              if (item.vencimiento && item.concesion) {
                fecha_vencimiento = item.vencimiento;
                fecha_vencimiento = fecha_vencimiento.replace(
                  /(\d{2})\/(\d{2})\/(\d{4})/,
                  "01/$2/$3"
                );
                fecha_concesion = fecha_concesion.replace(
                  /(\d{2})\/(\d{2})\/(\d{4})/,
                  "01/$2/$3"
                );
                let term_in_months = moment(
                  fecha_vencimiento,
                  "DD/MM/YYYY"
                ).diff(moment(fecha_concesion, "DD/MM/YYYY"), "months");
                item.plazo = term_in_months.toFixed(0);
              }
              item.quota = 0;
              // v.pmt(item.tasa_anual / 12, item.plazo, item.capital_original) *
              // -1;
              return item;
            });

            /** Prestamos */
            let loan_conditions = v.module_settings.loan_conditions || [];
            let loans = v
              .filter_data(data.detalle, loan_conditions, "loan")
              .filter(
                (item) =>
                  !hipotecas.find((hipoteca) => hipoteca.id === item.id) &&
                  !cancelados.find((cancelado) => cancelado.id === item.id)
              );
            data.prestamos = loans.map((item) => {
              item.tasa_anual = 0.2; // Assuming a fixed annual rate for non-credit card debts
              let fecha_concesion = item.concesion;
              let fecha_vencimiento = "";
              if (item.vencimiento) {
                fecha_vencimiento = item.vencimiento;
                fecha_vencimiento = fecha_vencimiento.replace(
                  /(\d{2})\/(\d{2})\/(\d{4})/,
                  "01/$2/$3"
                );
                fecha_concesion = fecha_concesion.replace(
                  /(\d{2})\/(\d{2})\/(\d{4})/,
                  "01/$2/$3"
                );
                let term_in_months = moment(
                  fecha_vencimiento,
                  "DD/MM/YYYY"
                ).diff(moment(fecha_concesion, "DD/MM/YYYY"), "months");
                item.plazo = term_in_months.toFixed(0);
              }
              item.quota =
                v.pmt(item.tasa_anual / 12, item.plazo, item.capital_original) *
                -1;
              return item;
            });

            /** Otros activos */
            let no_definidos = data.detalle.filter((item) => {
              return (
                !tarjetas.find((tarjeta) => tarjeta.id === item.id) &&
                !loans.find((loan) => loan.id === item.id) &&
                !hipotecas.find((hipoteca) => hipoteca.id === item.id) &&
                !extras.find((extra) => extra.id === item.id) &&
                !cancelados.find((cancelado) => cancelado.id === item.id)
              );
            });
            data.no_definidos = no_definidos.map((item) => {
              return item;
            });
          }

          v.meta = data;
          v.$refs.original.set_meta({ ...data });
          v.$refs.original.set_debt_buying({
            ...debt_level.electronic_signature_data,
          });
          if (debt_level.user_data && debt_level.user_data.original_data)
            v.$refs.original.set_editable({
              ...debt_level.user_data.original_data,
            });

          let final_data =
            debt_level.user_data?.edited_meta ||
            JSON.parse(JSON.stringify(data));
          v.$refs.edited.set_meta(final_data);
          v.$refs.edited.set_debt_buying({
            ...debt_level.electronic_signature_data,
          });
          if (debt_level.user_data && debt_level.user_data.edited_data)
            v.$refs.edited.set_editable(debt_level.user_data.edited_data);

          v.$root.loader(false);
        })
        .catch((error) => {
          v.$root.loader(false);
          v.error = generate_error_message_text(error);
        })
        .finally(() => {});
    },
    pmt(rate, nper, pv) {
      if (rate === 0) return -(pv / nper);
      const factor = Math.pow(1 + rate, nper);
      return -(pv * rate * factor) / (factor - 1);
    },
    format_currency,
    format_date_time,
    filter_data(data, conditions, type) {
      return data.filter((item) => {
        return conditions.some((condition) => {
          let valid_active_type = condition.active_type ? false : true;
          let valid_warranty = condition.warranty_type ? false : true;
          let valid_segment = condition.segment ? false : true;

          if (condition.active_type && item.tipo_activo) {
            valid_active_type =
              item.tipo_activo.toUpperCase() ===
              condition.active_type.toUpperCase();
          }

          if (condition.warranty_type && item.tipo_garantia) {
            valid_warranty =
              item.tipo_garantia.toUpperCase() ===
              condition.warranty_type.toUpperCase();
          }

          if (condition.segment && item.segmento) {
            valid_segment =
              item.segmento.toUpperCase() === condition.segment.toUpperCase();
          }

          let result = valid_active_type && valid_warranty && valid_segment;
          // if (type == "mortgage") {
          // if (result)
          //   console.log(
          //     `Result: ${result}. Item: ${item.id}, Active Type Valid: ${valid_active_type}, Warranty Valid: ${valid_warranty}, Segment Valid: ${valid_segment}`
          //   );
          // }
          return result;
        });
      });
    },
    save() {
      let v = this;
      let original_data = v.$refs.original.get_data();
      let original_meta = v.$refs.original.get_meta();
      let original_summary = v.$refs.original.get_summary();
      let edited_data = v.$refs.edited.get_data();
      let edited_meta = v.$refs.edited.get_meta();
      let edited_summary = v.$refs.edited.get_summary();
      v.$root.loader(true);
      post("/debt-level", {
        uuid: v.$route.params.uuid,
        data: {
          original_data,
          original_meta,
          original_summary,
          edited_data,
          edited_meta,
          edited_summary,
        },
      })
        .then((/* response */) => {
          success_message(v.$t("data saved successfully"));
          v.refresh();
        })
        .catch((error) => {
          error_message(error);
        })
        .finally(() => {
          v.$root.loader(false);
        });
    },
  },
  mounted() {
    let v = this;
    let uuid = v.$route.params.uuid;
    v.$root.loader(true);
    if (uuid) {
      get_configuration(v)
        .then((settings) => {
          v.module_settings = settings;
          v.$refs.original.set_module_settings(settings);
          // v.$refs.edited.set_module_settings(settings);
          v.refresh();
        })
        .catch((error) => {
          console.error(error);
        })
        .finally(() => v.$root.loader(false));
    } else {
      error_message(v.$t("uuid not provided"));
      v.$root.loader(false);
    }
  },
  created() {},
};
</script>

<style scoped></style>
